/**
 * ResetPasswordPage — token-based "Set a new password" screen reached via
 * the email link sent by /auth/forgot-password.
 *
 * Behavioral contract (UNCHANGED):
 *   • Reads the `token` query param. Without it, submission is blocked
 *     and a warn banner explains why.
 *   • POSTs `{ token, new_password }` to /api/auth/reset-password and
 *     shows the success state on 2xx. On error, surfaces the backend
 *     detail (non-technical strings only).
 *
 * Visual rebuild from the old shadcn-Card skin into the same AuthShell
 * used by Login/Register. Adds:
 *   • Password visibility toggle on both fields
 *   • Live "passwords match" indicator (no submit roundtrip)
 *   • Inline minimum-length helper that turns green at 6+ chars
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import AuthShell, { AuthHeader, AuthAlert } from '@/components/auth/AuthShell';
import AuthInput from '@/components/auth/AuthInput';
import AuthSubmit from '@/components/auth/AuthSubmit';
import { Button } from '@/components/ui/button';
import { PasswordRules } from '@/components/auth/PasswordRules';
import { PASSWORD_MIN_LENGTH, passwordRuleError } from '@/lib/passwordPolicy';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Live validation state — drives the inline indicators below the
  // password fields. We don't gate submit on these (the form's required
  // attributes + server-side validation handle that), they're visual hints.
  const rulesOK = passwordRuleError(newPassword) === null;
  const matches = useMemo(
    () => confirmPassword.length > 0 && newPassword === confirmPassword,
    [newPassword, confirmPassword],
  );
  const mismatched = confirmPassword.length > 0 && !matches;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!rulesOK) { setError(passwordRuleError(newPassword)); return; }
    if (!matches) { setError('Passwords do not match'); return; }
    if (!token) { setError('Missing reset token. Please use the link from your reset email.'); return; }

    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="reset-password-page">
      {success ? (
        <SuccessView onContinue={() => navigate('/login')} />
      ) : (
        <>
          <AuthHeader
            title="Set a New Password"
            subtitle="Create a new password for your account. Choose something secure you'll remember."
          />

          {!token && (
            <div className="mb-4">
              <AuthAlert kind="warn">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    No reset token found. Please use the &quot;Forgot password&quot; link on the
                    sign-in page to receive a fresh reset email.
                  </span>
                </div>
              </AuthAlert>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && <AuthAlert kind="error" testId="reset-error">{error}</AuthAlert>}

            <div>
              <AuthInput
                id="new-password"
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
                withVisibilityToggle
                testId="new-password-input"
              />
              <PasswordRules password={newPassword} testId="new-password-rules" />
            </div>

            <div>
              <AuthInput
                id="confirm-password"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Re-enter new password"
                withVisibilityToggle
                testId="confirm-password-input"
              />
              {confirmPassword.length > 0 && (
                <p
                  className="text-[10px] mt-1.5 transition-colors"
                  style={{ color: matches ? '#9DC4A8' : '#D4817A' }}
                  data-testid="confirm-password-match-hint"
                >
                  {matches ? '✓ Passwords match' : 'Passwords do not match'}
                </p>
              )}
            </div>

            <AuthSubmit
              loading={loading}
              disabled={!token || !rulesOK || mismatched}
              loadingLabel="Updating…"
              testId="reset-submit-button"
            >
              Update Password
            </AuthSubmit>

            <p className="text-center text-sm" style={{ color: '#71717A' }}>
              <Link
                to="/login"
                className="auth-link font-medium"
                style={{ color: '#4575A8' }}
                data-testid="back-to-login-link"
              >
                Back to Sign In
              </Link>
            </p>
          </form>
        </>
      )}
    </AuthShell>
  );
}

function SuccessView({ onContinue }) {
  return (
    <>
      <AuthHeader
        title="Password Updated"
        subtitle="You can now sign in with your new password."
      />
      <div className="space-y-5">
        <AuthAlert kind="success">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium" style={{ color: '#F2F0EB' }}>Your password has been updated</p>
              <p className="text-xs mt-0.5" style={{ color: '#A1A1A5' }}>
                For your security, all existing sessions remain valid. Use your new password the next time you sign in.
              </p>
            </div>
          </div>
        </AuthAlert>
        <Button
          data-testid="go-to-login-button"
          onClick={onContinue}
          className="auth-btn w-full h-11 text-sm font-medium rounded-lg bg-[#4575A8] hover:bg-[#5C8AC2] text-white"
        >
          Continue to Sign In
        </Button>
      </div>
    </>
  );
}
